/**
 * 4-week starter plans per MoneyMind persona — shown on the quiz results page.
 * Week 1 is fully visible to everyone. Weeks 2–4: titles + teasers are public,
 * `actions` render only for members (anonymous visitors see blurred placeholders,
 * real copy is never in their DOM).
 */
export const PERSONA_PLANS = {
  architect: {
    intro: "You already think in systems. This plan gives your money one — four weeks, one build.",
    weeks: [
      { title: "See the whole system", actions: [
        "Write down your current checking balance — today's number, no judgment. That's your baseline input.",
        "List every bill due in the next 30 days with its date and amount. You're mapping the machine before touching it.",
        "Mark your paydays on the same list. Now you can see the flow: what lands, what leaves, and when.",
      ]},
      { title: "Give every dollar a date", teaser: "Turn the map into a running plan that computes your balance after every entry.", actions: [
        "Enter your bills and paydays into your Spending Plan so the running balance does the math for you.",
        "Set your repeating entries once — rent, subscriptions, paychecks — so the system maintains itself.",
        "Do one 5-minute check-in: confirm next week's entries match reality, adjust anything that moved.",
      ]},
      { title: "Find the leaks", teaser: "Run the diagnostics: where the design quietly loses money.", actions: [
        "Run your Cash Flow Scan and read the leak report like a systems review — no blame, just data.",
        "Pick ONE leak and design a fix into the plan (a cap, a cancel, or a swap).",
        "Add an if/then rule for your most common disruption week.",
      ]},
      { title: "Extend the model", teaser: "Add the long-range modules: net worth and your first quarterly checkpoint.", actions: [
        "Enter what you own and what you owe — your Net Worth baseline becomes the system's scoreboard.",
        "Open your 5-year Wealth Plan and set this quarter's single checkpoint.",
        "Schedule a monthly 10-minute review. The system runs; you just inspect it.",
      ]},
    ],
  },
  explorer: {
    intro: "You move fast and hate cages. This plan builds guardrails, not walls — four weeks, still free to roam.",
    weeks: [
      { title: "Know your starting point", actions: [
        "Write down your checking balance right now — one honest number, ten seconds, done.",
        "List the bills coming in the next 30 days. Not to restrict you — so surprises stop stealing your freedom.",
        "Mark your paydays. Adventure is easier when you know exactly what's fuel and what's spoken for.",
      ]},
      { title: "Date every dollar", teaser: "A plan that flexes with you — see your balance ahead before you say yes.", actions: [
        "Enter bills and paydays into your Spending Plan — the running balance shows what's truly free to spend.",
        "Give yourself a named 'yes fund' entry: guilt-free money for the spontaneous stuff.",
        "One 5-minute weekly check-in — that's the entire commitment.",
      ]},
      { title: "Cut the boring losses", teaser: "Find the leaks quietly draining your adventure budget.", actions: [
        "Run your Cash Flow Scan — every leak it finds is money that could fund something fun instead.",
        "Cancel or cap one thing you stopped loving months ago.",
        "Write one if/then rule for travel or big-weekend weeks.",
      ]},
      { title: "Fund the next big thing", teaser: "Point the freed-up cash at a future worth exploring.", actions: [
        "Set your Net Worth baseline — watch it as your freedom number, not a report card.",
        "Pick one quarterly checkpoint in your Wealth Plan tied to something you actually want.",
        "Automate one transfer so growth happens while you're out living.",
      ]},
    ],
  },
  guardian: {
    intro: "You protect what you've built. This plan strengthens the walls — four weeks, steady and secure.",
    weeks: [
      { title: "Take honest inventory", actions: [
        "Write down your current checking balance — your watchtower number. Knowing beats worrying.",
        "List every bill due in the next 30 days with dates. Nothing can ambush what's already mapped.",
        "Mark your paydays alongside them. Security starts with seeing the whole month at once.",
      ]},
      { title: "Put the month on rails", teaser: "A running plan that shows your balance after every bill — before it happens.", actions: [
        "Enter bills and paydays into your Spending Plan; the projected balance is your early-warning system.",
        "Flag the tightest week of the month so it never catches you off guard.",
        "Do a 5-minute Sunday check-in to confirm the week ahead is covered.",
      ]},
      { title: "Seal the gaps", teaser: "Find the small leaks before they become cracks.", actions: [
        "Run your Cash Flow Scan and review each flagged leak calmly — most cost less to fix than to worry about.",
        "Set one bill to autopay so on-time payment is guaranteed, not remembered.",
        "Add an if/then rule for surprise-expense weeks.",
      ]},
      { title: "Grow the reserves", teaser: "Turn protection into progress: baseline, cushion, checkpoint.", actions: [
        "Enter your Net Worth baseline — assets minus debts, tracked monthly.",
        "Set a cushion goal: one month of essentials, built one payday at a time.",
        "Choose your first quarterly Wealth Plan checkpoint and let it run.",
      ]},
    ],
  },
  connector: {
    intro: "You take care of everyone. This plan makes sure you're on the list too — four weeks, no guilt.",
    weeks: [
      { title: "See your own numbers first", actions: [
        "Write down your checking balance — just for you, nobody else's needs in the frame yet.",
        "List your bills for the next 30 days with dates. Your obligations, clearly separated from everyone else's.",
        "Mark your paydays. You can't pour from an empty account — this is where yours fills.",
      ]},
      { title: "Give your dollars a date", teaser: "A plan that funds your generosity without draining your future.", actions: [
        "Enter bills and paydays into your Spending Plan — see your real balance before anyone asks for it.",
        "Create a named 'giving' entry with a monthly amount — generosity by design, not by depletion.",
        "One 5-minute weekly check-in keeps your yes's honest.",
      ]},
      { title: "Plug the people-pleasing leaks", teaser: "Find where saying yes quietly costs more than you meant to give.", actions: [
        "Run your Cash Flow Scan and look at the leaks without judgment — patterns, not verdicts.",
        "Practice one scripted no this week: 'It's not in my plan this month.' The plan takes the blame.",
        "Add an if/then rule for the requests that always find you.",
      ]},
      { title: "Build your own safety net", teaser: "The strongest thing you can give people is your own stability.", actions: [
        "Set your Net Worth baseline — your foundation, measured monthly.",
        "Start a cushion goal so helping others never means endangering yourself.",
        "Pick one quarterly Wealth Plan checkpoint that's purely yours.",
      ]},
    ],
  },
  dreamer: {
    intro: "Your vision is the easy part. This plan builds the bridge to it — four weeks, small steps, real motion.",
    weeks: [
      { title: "Start where your feet are", actions: [
        "Write down your current checking balance — the dream needs a starting coordinate, and this is it.",
        "List every bill due in the next 30 days. Ten minutes of reality that makes the vision fundable.",
        "Mark your paydays. Now the gap between today and the dream has actual numbers on it.",
      ]},
      { title: "Turn the vision into dates", teaser: "A running plan that shows the future you're already building.", actions: [
        "Enter bills and paydays into your Spending Plan — watch your projected balance stretch 30 days ahead.",
        "Name one entry after your dream and give it a small monthly amount. Funded dreams stop being wishes.",
        "One 5-minute weekly check-in — momentum loves rhythm.",
      ]},
      { title: "Redirect the drift", teaser: "Find the leaks quietly taxing your future.", actions: [
        "Run your Cash Flow Scan — every leak found is dream-fuel recovered.",
        "Move one leak's worth of money into your dream entry.",
        "Write one if/then rule for low-motivation weeks — the plan works even when inspiration doesn't.",
      ]},
      { title: "Give the dream a timeline", teaser: "Five years, twenty checkpoints — the vision becomes a route.", actions: [
        "Set your Net Worth baseline — progress you can watch, not just imagine.",
        "Open your 5-year Wealth Plan and set the first quarterly checkpoint toward the big one.",
        "Schedule a monthly review: 10 minutes to keep the bridge growing.",
      ]},
    ],
  },
  minimalist: {
    intro: "You already spend with intention. This plan removes the last of the noise — four weeks, nothing extra.",
    weeks: [
      { title: "One page, whole picture", actions: [
        "Write down your checking balance — one number, one truth.",
        "List every bill due in the next 30 days. Most people are surprised how short the essential list really is.",
        "Mark your paydays. Income, obligations, done — the entire picture on one page.",
      ]},
      { title: "A plan with no clutter", teaser: "Every dollar gets a date; everything else gets deleted.", actions: [
        "Enter bills and paydays into your Spending Plan — a clean ledger with a running balance, nothing decorative.",
        "Set repeats once so the plan maintains itself quietly.",
        "One 5-minute weekly check-in. That's the whole practice.",
      ]},
      { title: "Subtract the leaks", teaser: "Find what snuck in that doesn't earn its keep.", actions: [
        "Run your Cash Flow Scan — treat every leak as clutter with a price tag.",
        "Cancel one subscription or fee you'd forgotten existed.",
        "Add one if/then rule so disruptions don't add complexity back.",
      ]},
      { title: "Quiet growth", teaser: "A baseline, a cushion, one checkpoint — enough, on purpose.", actions: [
        "Set your Net Worth baseline: own minus owe, tracked monthly.",
        "Automate one modest transfer toward your cushion — growth without maintenance.",
        "Pick a single quarterly Wealth Plan checkpoint. One is enough.",
      ]},
    ],
  },
  peacekeeper: {
    intro: "No pressure, no panic — just four gentle weeks that trade money anxiety for quiet clarity.",
    weeks: [
      { title: "Look once, gently", actions: [
        "Write down your current checking balance. One look, one number — the dread of not-knowing costs more than any balance ever does.",
        "List the bills due in the next 30 days with their dates. On paper they're smaller than they feel in your head.",
        "Mark your paydays next to them. Seeing money arriving is part of the picture too.",
      ]},
      { title: "Let the plan hold the worry", teaser: "A running balance that watches the month so you don't have to.", actions: [
        "Enter your bills and paydays into your Spending Plan — the projected balance means no more panicked login checks.",
        "Find your calmest 5 minutes (Sunday works) and make that your weekly check-in.",
        "Notice one week ahead that's fully covered — let yourself feel that.",
      ]},
      { title: "Soften the surprises", teaser: "Find the quiet leaks — kindly, with no verdicts attached.", actions: [
        "Run your Cash Flow Scan and read it like a friend's note, not a report card.",
        "Set one bill to autopay so one worry becomes zero decisions.",
        "Write one gentle if/then plan for the week the routine wobbles.",
      ]},
      { title: "Grow something quietly", teaser: "A baseline and one small checkpoint — progress at a peaceful pace.", actions: [
        "Set your Net Worth baseline — check it monthly, not daily. The trend line is the truth.",
        "Start one small automatic transfer to savings — it grows while you rest.",
        "Choose one quarterly Wealth Plan checkpoint. Just one. That's plenty.",
      ]},
    ],
  },
  achiever: {
    intro: "You don't need motivation — you need a scoreboard. Four weeks, measurable wins, visible progress.",
    weeks: [
      { title: "Establish the baseline", actions: [
        "Write down your current checking balance — every win streak starts with an honest starting stat.",
        "List all bills due in the next 30 days with dates and amounts. Complete data, nothing missed.",
        "Mark your paydays. Now you know your inputs — time to optimize the outputs.",
      ]},
      { title: "Build the scoreboard", teaser: "A running plan that turns every week into a measurable win.", actions: [
        "Enter bills and paydays into your Spending Plan — your projected end-of-month balance is the score to beat.",
        "Set a target for month-end and track the gap weekly.",
        "Log a 5-minute weekly check-in — consistency is the streak that feeds every other stat.",
      ]},
      { title: "Eliminate the drag", teaser: "Find and cut the leaks costing you performance.", actions: [
        "Run your Cash Flow Scan and rank the leaks by monthly cost — then close the biggest one first.",
        "Redirect the recovered money to your highest-priority goal, same week.",
        "Add an if/then rule for high-spend weeks so one busy stretch never breaks the streak.",
      ]},
      { title: "Compound the wins", teaser: "Net worth up and to the right — with quarterly checkpoints to hit.", actions: [
        "Set your Net Worth baseline — this is the number that compounds over time.",
        "Open your 5-year Wealth Plan and set an ambitious and realistic first checkpoint.",
        "Book a monthly 10-minute review: measure, adjust, repeat.",
      ]},
    ],
  },
};
